import React, { useState } from 'react';
import { 
  Users, 
  Target, 
  Brain, 
  Shield, 
  CheckCircle, 
  Star,
  ArrowRight,
  Heart,
  TrendingUp,
  Clock,
  Award,
  BookOpen,
  Phone,
  Mail,
  Building
} from 'lucide-react';

const CoachingStaffWellbeingPage: React.FC = () => {
  const [selectedService, setSelectedService] = useState<string | null>(null);

  const coachingServices = [
    {
      id: 'executive-coaching',
      title: 'Executive Coaching',
      icon: <Target className="h-8 w-8 text-blue-600" />,
      description: 'One-on-one executive coaching to enhance leadership skills and performance.',
      features: [
        'Leadership development',
        'Strategic thinking enhancement',
        'Communication skills improvement',
        'Change management support',
        'Performance optimization'
      ],
      duration: '6-12 month programs',
      format: 'Individual sessions'
    },
    {
      id: 'team-coaching',
      title: 'Team Coaching',
      icon: <Users className="h-8 w-8 text-green-600" />,
      description: 'Team-based coaching to improve collaboration, communication and performance.',
      features: [
        'Team dynamics improvement',
        'Conflict resolution skills',
        'Collaborative goal setting',
        'Communication enhancement',
        'Trust building exercises'
      ],
      duration: '3-6 month programs',
      format: 'Group sessions'
    },
    {
      id: 'wellbeing-programs',
      title: 'Staff Wellbeing Programs',
      icon: <Heart className="h-8 w-8 text-red-600" />,
      description: 'Comprehensive wellbeing initiatives to support employee mental health and resilience.',
      features: [
        'Stress management workshops',
        'Mindfulness training',
        'Resilience building',
        'Work-life balance support',
        'Mental health awareness'
      ],
      duration: 'Ongoing support',
      format: 'Workshops & individual support'
    },
    {
      id: 'organizational-development',
      title: 'Organizational Development',
      icon: <Building className="h-8 w-8 text-purple-600" />,
      description: 'Strategic organizational coaching to enhance culture and performance.',
      features: [
        'Culture transformation',
        'Change management',
        'Performance improvement',
        'Employee engagement',
        'Organizational health assessment'
      ],
      duration: '12+ month partnerships',
      format: 'Organizational consultancy'
    }
  ];

  const wellbeingAreas = [
    {
      area: 'Mental Health Support',
      description: 'Comprehensive mental health support services for employees at all levels.',
      services: [
        'Individual counseling sessions',
        'Group therapy programs',
        'Mental health first aid training',
        'Crisis intervention support',
        'Stress and anxiety management'
      ],
      icon: <Brain className="h-6 w-6 text-blue-500" />
    },
    {
      area: 'Physical Wellbeing',
      description: 'Programs focused on physical health and its connection to mental wellbeing.',
      services: [
        'Ergonomic assessments',
        'Fitness and wellness programs',
        'Nutrition guidance',
        'Sleep hygiene education',
        'Health screening initiatives'
      ],
      icon: <Shield className="h-6 w-6 text-green-500" />
    },
    {
      area: 'Work-Life Balance',
      description: 'Support for achieving healthy work-life integration and boundary setting.',
      services: [
        'Time management coaching',
        'Boundary setting workshops',
        'Flexible working arrangements',
        'Family support programs',
        'Career transition support'
      ],
      icon: <Star className="h-6 w-6 text-yellow-500" />
    },
    {
      area: 'Professional Development',
      description: 'Growth-focused programs to enhance skills and career progression.',
      services: [
        'Skills development workshops',
        'Leadership training programs',
        'Mentorship opportunities',
        'Career coaching sessions',
        'Professional certification support'
      ],
      icon: <TrendingUp className="h-6 w-6 text-purple-500" />
    }
  ];

  const benefits = [
    {
      category: 'For Employees',
      benefits: [
        'Improved job satisfaction and engagement',
        'Enhanced stress management skills',
        'Better work-life balance',
        'Increased resilience and wellbeing',
        'Career development opportunities',
        'Stronger professional relationships'
      ],
      color: 'blue'
    },
    {
      category: 'For Organizations',
      benefits: [
        'Reduced absenteeism and turnover',
        'Improved productivity and performance',
        'Enhanced company culture',
        'Better employee retention',
        'Reduced healthcare costs',
        'Stronger organizational reputation'
      ],
      color: 'green'
    }
  ];

  const programStructure = [
    {
      phase: 'Assessment & Planning',
      description: 'Comprehensive assessment of organizational needs and wellbeing goals.',
      duration: '2-4 weeks',
      activities: [
        'Organizational wellbeing assessment',
        'Employee surveys and interviews',
        'Stakeholder consultation',
        'Program design and planning',
        'Resource allocation'
      ]
    },
    {
      phase: 'Implementation',
      description: 'Delivery of tailored coaching and wellbeing programs.',
      duration: '3-12 months',
      activities: [
        'Launch of wellbeing initiatives',
        'Coaching session delivery',
        'Workshop and training delivery',
        'Progress monitoring',
        'Regular check-ins and adjustments'
      ]
    },
    {
      phase: 'Evaluation & Sustainability',
      description: 'Assessment of outcomes and establishment of sustainable practices.',
      duration: 'Ongoing',
      activities: [
        'Impact measurement and evaluation',
        'Feedback collection and analysis',
        'Program refinement',
        'Sustainability planning',
        'Long-term support arrangements'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Users className="h-12 w-12 text-blue-600" />
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900">
              Coaching and Staff Wellbeing
            </h1>
          </div>
          <h2 className="text-2xl lg:text-3xl text-blue-600 font-semibold mb-6">
            Coaching and Staff Wellbeing Services
          </h2>
          <p className="text-xl text-gray-700 max-w-4xl mx-auto mb-8">
            Comprehensive coaching and wellbeing solutions designed to enhance employee performance, 
            satisfaction, and organizational success through targeted support and development programs.
          </p>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-4xl mx-auto">
            <div className="flex items-center gap-3 justify-center mb-4">
              <Heart className="h-6 w-6 text-blue-600" />
              <h3 className="text-lg font-semibold text-blue-800">Investing in Your People</h3>
            </div>
            <p className="text-blue-700">
              Our evidence-based coaching and wellbeing programs are designed to create healthier, 
              more engaged workforces while driving organizational performance and success.
            </p>
          </div>
        </div>

        {/* Services Overview */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Our Coaching & Wellbeing Services
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {coachingServices.map((service) => (
              <div 
                key={service.id} 
                className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow cursor-pointer"
                onClick={() => setSelectedService(selectedService === service.id ? null : service.id)}
              >
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    {service.icon}
                    <h3 className="text-xl font-bold text-gray-900">{service.title}</h3>
                  </div>
                  
                  <p className="text-gray-700 mb-4">{service.description}</p>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-gray-600">Duration</p>
                      <p className="font-semibold text-gray-900">{service.duration}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Format</p>
                      <p className="font-semibold text-gray-900">{service.format}</p>
                    </div>
                  </div>
                  
                  {selectedService === service.id && (
                    <div className="space-y-4 border-t border-gray-200 pt-4">
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Key Features:</h4>
                        <ul className="text-sm text-gray-700 space-y-1">
                          {service.features.map((feature, index) => (
                            <li key={index} className="flex items-start gap-2">
                              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                  
                  <div className="mt-4 flex items-center text-blue-600 text-sm font-medium">
                    {selectedService === service.id ? 'Click to collapse' : 'Click to learn more'}
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Wellbeing Areas */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Staff Wellbeing Focus Areas
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {wellbeingAreas.map((area, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center gap-3 mb-4">
                  {area.icon}
                  <h3 className="text-lg font-bold text-gray-900">{area.area}</h3>
                </div>
                
                <p className="text-gray-700 mb-4">{area.description}</p>
                
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Services Include:</h4>
                  <ul className="space-y-2">
                    {area.services.map((service, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-gray-700 text-sm">
                        <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                        {service}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Benefits */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Benefits of Our Programs
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className={`bg-white rounded-lg shadow-lg p-8 border-l-4 border-${benefit.color}-500`}>
                <h3 className={`text-xl font-bold text-${benefit.color}-800 mb-6`}>{benefit.category}</h3>
                
                <ul className="space-y-3">
                  {benefit.benefits.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Star className={`h-5 w-5 text-${benefit.color}-500 mt-0.5 flex-shrink-0`} />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Program Structure */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Our Program Approach
          </h2>
          
          <div className="space-y-8">
            {programStructure.map((phase, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="bg-blue-100 text-blue-800 rounded-full w-10 h-10 flex items-center justify-center text-lg font-bold">
                    {index + 1}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{phase.phase}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <Clock className="h-4 w-4 text-gray-500" />
                      <span className="text-sm text-gray-600">{phase.duration}</span>
                    </div>
                  </div>
                </div>
                
                <p className="text-gray-700 mb-6">{phase.description}</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {phase.activities.map((activity, idx) => (
                    <div key={idx} className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <span className="text-sm text-gray-700">{activity}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Ready to Transform Your Workplace?
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              Contact us to discuss how our coaching and staff wellbeing services can benefit your organization. 
              We'll work with you to create a tailored program that meets your specific needs and goals.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-blue-800 mb-4">Get Started Today</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Phone className="h-5 w-5 text-blue-600" />
                  <span className="text-blue-700">0333 800 3006</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-5 w-5 text-blue-600" />
                  <span className="text-blue-700">0333 789 0012</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-blue-600" />
                  <span className="text-blue-700">info@healthcity.uk</span>
                </div>
              </div>
            </div>
            
            <div className="bg-green-50 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-green-800 mb-4">What Happens Next?</h3>
              <ul className="space-y-2 text-green-700">
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  Initial consultation to understand your needs
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  Customized program proposal
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  Program implementation and support
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  Ongoing evaluation and refinement
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoachingStaffWellbeingPage;